import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { appointmentsApi, Appointment } from '../services/appointmentsService';
import { useTenantConfig } from '../contexts/TenantConfigContext';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;

  @media (max-width: 767px) {
    padding: 30px 20px;
  }
`;

const Icon = styled.div<{ type: 'success' | 'error' | 'loading' }>`
  font-size: 64px;
  margin-bottom: 20px;
  animation: ${props => props.type === 'loading' ? 'spin 1s linear infinite' : 'none'};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 12px 0;
`;

const Message = styled.p`
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const DetailsCard = styled.div`
  background: var(--background-color);
  border-radius: var(--border-radius-md);
  padding: 20px;
  text-align: right;
  margin-bottom: 20px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const Value = styled.span`
  color: var(--text-secondary);
`;

const ContactInfo = styled.div`
  background: var(--primary-light);
  border-radius: var(--border-radius-md);
  padding: 16px;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const AppointmentConfirmationPage: React.FC = () => {
  // טעינת הגדרות החברה (per-tenant) - אימייל וטלפון תמיכה מ-SystemParameters
  const { config } = useTenantConfig();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action'); // 'confirm' or 'cancel'

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<Appointment | null>(null);

  useEffect(() => {
    const processConfirmation = async () => {
      if (!token || !action) {
        setError('קישור לא תקין');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let result: Appointment;

        if (action === 'confirm') {
          result = await appointmentsApi.confirmAppointment(token);
        } else if (action === 'cancel') {
          result = await appointmentsApi.cancelAppointment(token);
        } else {
          throw new Error('פעולה לא חוקית');
        }

        setAppointment(result);
        setSuccess(true);
      } catch (err: any) {
        console.error('Error processing confirmation:', err);
        setError(err.response?.data || 'שגיאה בעיבוד הבקשה');
      } finally {
        setLoading(false);
      }
    };

    processConfirmation();
  }, [token, action]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'לא נקבע';
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <Card>
          <Icon type="loading">⏳</Icon>
          <Title>מעבד...</Title>
          <Message>אנא המתן, מעבדים את הבקשה שלך.</Message>
        </Card>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Card>
          <Icon type="error">❌</Icon>
          <Title>שגיאה</Title>
          <Message>{error}</Message>
          <ContactInfo>
            אם אתה חושב שזו טעות, אנא צור קשר עם משרדנו:
            <br />
            <strong>טלפון:</strong> {config?.supportPhone || ''}
            <br />
            <strong>דוא״ל:</strong> {config?.supportEmail || ''}
          </ContactInfo>
        </Card>
      </PageContainer>
    );
  }

  if (success && appointment) {
    const isConfirmed = action === 'confirm';

    return (
      <PageContainer>
        <Card>
          <Icon type="success">{isConfirmed ? '✅' : '❌'}</Icon>
          <Title>
            {isConfirmed ? 'הפגישה אושרה!' : 'הפגישה בוטלה'}
          </Title>
          <Message>
            {isConfirmed
              ? 'קיבלנו את האישור שלך. הפגישה נרשמה במערכת שלנו.'
              : 'הפגישה בוטלה בהצלחה. ניצור איתך קשר לתאום פגישה חדשה.'}
          </Message>

          <DetailsCard>
            <DetailRow>
              <Label>רופא:</Label>
              <Value>{appointment.doctorName}</Value>
            </DetailRow>
            <DetailRow>
              <Label>תאריך ושעה:</Label>
              <Value>{formatDate(appointment.appointmentDateTime)}</Value>
            </DetailRow>
            {appointment.meetingTypeName && (
              <DetailRow>
                <Label>סוג פגישה:</Label>
                <Value>{appointment.meetingTypeName}</Value>
              </DetailRow>
            )}
            {appointment.virtualMeetingLink && (
              <DetailRow>
                <Label>קישור:</Label>
                <Value>
                  <a href={appointment.virtualMeetingLink} target="_blank" rel="noopener noreferrer">
                    {appointment.virtualMeetingLink}
                  </a>
                </Value>
              </DetailRow>
            )}
            {appointment.address && (
              <DetailRow>
                <Label>כתובת:</Label>
                <Value>{appointment.address}</Value>
              </DetailRow>
            )}
          </DetailsCard>

          <ContactInfo>
            {isConfirmed ? (
              <>
                אנחנו מצפים לראותך! אם יש צורך לשנות את הפגישה, צור קשר:
                <br />
                <strong>טלפון:</strong> {config?.supportPhone || ''}
              </>
            ) : (
              <>
                ניצור איתך קשר בקרוב כדי לתאם פגישה חדשה.
                <br />
                <strong>טלפון:</strong> {config?.supportPhone || ''}
              </>
            )}
          </ContactInfo>
        </Card>
      </PageContainer>
    );
  }

  return null;
};

export default AppointmentConfirmationPage;
